import { useSpherePermission, resetSpherePermissionCache } from './useSpherePermission';
import type { SpherePermissionAction } from './useSpherePermission';

export interface UsePropertyAccessResult {
  canWrite: boolean;
  loading: boolean;
}

/**
 * Clear cached property-access results. Delegates to the underlying sphere
 * permission cache. Call on logout / impersonation / identity change.
 */
export function resetPropertyAccessCache(): void {
  resetSpherePermissionCache();
}

const SPHERE_PERMISSION_ACTIONS: ReadonlySet<SpherePermissionAction> = new Set([
  'discover',
  'view',
  'edit',
  'delete',
  'manage_security',
]);

function isSpherePermission(value: string | undefined): value is SpherePermissionAction {
  return value !== undefined && SPHERE_PERMISSION_ACTIONS.has(value as SpherePermissionAction);
}

/**
 * Returns whether the authenticated user can write to `prop` on `sphereId`.
 *
 * Reads `prop.access.write` from the schema metadata (already on the wire) and
 * delegates to `useSpherePermission` — no SpiceDB identifiers in the bundle.
 *
 * Sphere-applied schemas may only declare permissions in PERMISSIONS_BY_LABEL.sphere
 * (validated server-side at publish time). Runtime narrowing here keeps the type
 * contract honest and skips the HTTP check for any out-of-band declarations.
 *
 * Fails closed: any error or missing `sphereId` resolves to `canWrite: false`.
 */
export function usePropertyAccess(
  sphereId: string | undefined,
  prop: { access?: { write?: string } } | undefined
): UsePropertyAccessResult {
  const declaredWrite = prop?.access?.write;
  const writeAction = isSpherePermission(declaredWrite) ? declaredWrite : undefined;

  // Skip the HTTP check entirely when no sphere-permission write gate is declared.
  const checkSphereId = writeAction ? sphereId : undefined;

  const { allowed, loading } = useSpherePermission(
    checkSphereId,
    // Action arg is required; unused when checkSphereId is undefined.
    writeAction ?? 'edit'
  );

  if (!writeAction) {
    return { canWrite: true, loading: false };
  }

  return { canWrite: allowed, loading };
}
